import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ListChecks } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

/**
 * Optionale Checkliste innerhalb eines Tasks.
 * Speichert bei jeder Aenderung ueber onUpdate(taskId, { checklist }).
 */
const TaskChecklist = ({ task, onUpdate }) => {
    const [items, setItems] = useState(task.checklist || []);
    const [newText, setNewText] = useState('');

    // Externe Aenderungen (z.B. nach Reload) uebernehmen
    useEffect(() => {
        setItems(task.checklist || []);
    }, [task.id, task.checklist]);

    const save = async (next) => {
        setItems(next);
        await onUpdate(task.id, { checklist: next });
    };

    const addItem = async () => {
        const text = newText.trim();
        if (!text) return;
        setNewText('');
        await save([...items, { id: newId(), text, done: false }]);
    };

    const toggle = (id) => save(items.map(i => (i.id === id ? { ...i, done: !i.done } : i)));
    const remove = (id) => save(items.filter(i => i.id !== id));

    const doneCount = items.filter(i => i.done).length;
    const progress = items.length > 0 ? Math.round((doneCount / items.length) * 100) : 0;

    return (
        <div className="space-y-3" data-testid="task-checklist">
            {items.length > 0 && (
                <div className="flex items-center gap-3">
                    <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div
                            className={`h-full rounded-full transition-all ${progress === 100 ? 'bg-green-500' : 'bg-primary'}`}
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                    <span className="text-xs text-muted-foreground tabular-nums">
                        {doneCount}/{items.length}
                    </span>
                </div>
            )}

            <div className="space-y-1">
                {items.map(item => (
                    <div
                        key={item.id}
                        className="group flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50"
                        data-testid={`checklist-item-${item.id}`}
                    >
                        <Checkbox
                            checked={item.done}
                            onCheckedChange={() => toggle(item.id)}
                            className="w-4 h-4"
                        />
                        <span className={`flex-1 text-sm ${item.done ? 'line-through text-muted-foreground' : ''}`}>
                            {item.text}
                        </span>
                        <button
                            type="button"
                            onClick={() => remove(item.id)}
                            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                            title="Punkt entfernen"
                        >
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ))}
                {items.length === 0 && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5 px-2">
                        <ListChecks className="w-3.5 h-3.5" />
                        Noch keine Punkte. Schreib unten etwas zum Abhaken rein.
                    </p>
                )}
            </div>

            <div className="flex gap-2">
                <Input
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
                    placeholder="Neuer Punkt..."
                    className="h-8 text-sm"
                    data-testid="checklist-new-item"
                />
                <Button size="sm" variant="outline" className="h-8" onClick={addItem} disabled={!newText.trim()}>
                    <Plus className="w-4 h-4" />
                </Button>
            </div>
        </div>
    );
};

export default TaskChecklist;
